import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { JANE_DEFINED_ROWS } from '@iheartjane/menu';
import {
  Button,
  Card,
  Flex,
  Modal,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import {
  Form,
  FormValidationError,
  useForm,
} from '@iheartjane/reefer-hook-form';
import get from 'lodash-es/get';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useParams } from 'react-router';

import {
  useCreateMenuRow,
  useStoreSettings,
  useUpdateMenuRow,
} from '@jane/business-admin/data-access';
import {
  useCatchErrorsWithManager,
  useGetProductDetails,
  useMenuRowType,
  useModalActionsWithTracking,
} from '@jane/business-admin/hooks';
import type {
  MenuProductForProductsTable,
  MenuRow,
} from '@jane/business-admin/types';
import {
  ErrorReasons,
  EventNames,
  ModalNames,
  normalizePath,
  parseValidationErrors,
  track,
} from '@jane/business-admin/util';
/* eslint-disable-next-line no-restricted-imports */
import type { NuProductType } from '@jane/shared/models';

import { ConfirmWrapperWithTracking } from '../../../../ConfirmWrapperWithTracking';
import type { ProductSearchSelection } from '../../../../ProductSearchModalUpdated';
import { ProductSearchModalUpdated } from '../../../../ProductSearchModalUpdated';
import { MenuRowConditionsCard } from '../../../../menuRow/MenuRowConditionsCard';
import { ScheduleCard } from '../../../../shared/schedule/ScheduleCard';
import { convert12HourTimeTo24HourTime } from '../../../../shared/schedule/ScheduleTimeValidation';
import { DetailsCard } from './DetailsCard';
import { MenuRowProductTable } from './MenuRowProductTable';
import { ModalHeader } from './ModalHeader';
import {
  DEFAULT_ROW_SCHEDULE,
  formatSchedule,
  parseSchedule,
} from './scheduleHelpers';
import { useProductsByApplyToMode } from './useProductsByApplyToMode';
import type { ApplyToMode } from './useProductsByApplyToMode';

const FORM_ERROR_NAME = 'menu-row-errors';

type MenuRowProps = {
  allProductTypes: NuProductType[];
  closeModal: () => void;
  hasEmbeddedMenuRow?: boolean;
  productTypesLoaded: boolean;
  row?: MenuRow;
};

export const MenuRowModal = ({
  row,
  closeModal,
  hasEmbeddedMenuRow,
  productTypesLoaded,
  allProductTypes,
}: MenuRowProps) => {
  const isCreateMode = !row;

  const catchSubmitErrors = useCatchErrorsWithManager(
    `Error ${isCreateMode ? 'creating' : 'editing'} menu row. Please try again.`
  );
  const { id = '' } = useParams<'id'>();
  const { pathname } = useLocation();
  const toast = useToast();

  const shouldShowConditionsCard = useFlag(FLAGS.scMenuRowConditionsFe);
  const canUseRules = useFlag(FLAGS.cnUseRulesForMenuRows);
  const { getRowType, useMenuRowType: useMenuRowTypeFlag } = useMenuRowType();

  const { mutateAsync: createMenuRow, isSuccess: createSuccess } =
    useCreateMenuRow(id);
  const { mutateAsync: updateMenuRow, isSuccess: updateSuccess } =
    useUpdateMenuRow(id, row?.id || 0);
  const { data: storePayload, isFetching: storeSettingsLoading } =
    useStoreSettings(id);

  const [totalNumberProductsSelected, setTotalNumberProductsSelected] =
    useState(row?.menu_products_ids?.all.length || 0);

  const initialApplyTo = getDefaultApplyTo({
    isCreateMode,
    row,
    shouldShowConditionsCard,
    useMenuRowTypeFlag,
  });

  const {
    activeApplyToMode,
    selectedProductIds,
    setActiveApplyToMode,
    setProductsForMode,
    getProductsToSubmit,
  } = useProductsByApplyToMode({
    initialApplyTo: initialApplyTo as ApplyToMode | undefined,
    initialProductIds: row?.menu_products_ids?.all || [],
  });

  const {
    modalOpen: productSearchModalOpen,
    openModal: setProductSearchModalOpen,
    closeModal: setProductSearchModalClose,
  } = useModalActionsWithTracking(ModalNames.ProductSearch);

  const { data: productsForTable, isLoading: loadingProductsForTable } =
    useGetProductDetails(selectedProductIds, true);

  const productsForModal = useMemo(
    () =>
      productsForTable?.map<ProductSearchSelection & { inStock: boolean }>(
        ({ product_id, id, inventory_count }) => ({
          product_id: product_id.toString(),
          id: id.toString(),
          menu_product_id: id,
          inStock: inventory_count !== 0,
        })
      ) || [],
    [productsForTable]
  );

  const totalNumberProductsInStock = useMemo(
    () => productsForModal.filter((p) => p.inStock).length,
    [productsForModal]
  );

  const totalNumberProductsOutOfStock = useMemo(
    () => productsForModal.length - totalNumberProductsInStock,
    [productsForModal, totalNumberProductsInStock]
  );

  const rowProductType = useMemo(() => {
    if (!productTypesLoaded || isCreateMode) {
      return { display: {} };
    }

    if (row?.applicability_rules && row.applicability_rules.length > 0) {
      const hasRootSubtype = get(
        row,
        'rules.includes.[0].kinds.[0].root_subtype',
        null
      );

      let matchingType;

      if (canUseRules) {
        matchingType = !hasRootSubtype
          ? allProductTypes?.find(
              ({ product_type }) =>
                product_type ===
                get(row, 'rules.includes.[0].kinds.[0].kind', '')
            )
          : allProductTypes?.find(
              ({ product_subtype }) =>
                product_subtype ===
                get(row, 'rules.includes.[0].kinds.[0].root_subtype', '')
            );
      } else {
        matchingType =
          row?.applicability_rules[0].filter_type === 'type'
            ? allProductTypes?.find(
                ({ product_type }) =>
                  product_type ===
                  (row?.applicability_rules
                    ? row?.applicability_rules[0].filter_value
                    : '')
              )
            : allProductTypes?.find(
                ({ product_subtype }) =>
                  product_subtype ===
                  (row?.applicability_rules
                    ? row?.applicability_rules[0].filter_value
                    : '')
              );
      }

      if (matchingType) {
        return {
          display: {
            category: matchingType?.product_type,
            subcategory: matchingType?.product_subtype,
          },
        };
      }
    }

    return {
      display: {
        category: 'flower',
        subcategory: 'None',
      },
    };
  }, [isCreateMode, allProductTypes, row, productTypesLoaded]);

  const parsedSchedule = parseSchedule(row, isCreateMode);

  const formMethods = useForm({
    defaultValues: {
      ...row,
      ...rowProductType,
      ...parsedSchedule,
      custom_display_name: row?.custom_display_name || row?.row_type,
      ...(useMenuRowTypeFlag && {
        row_type: row?.row_type || 'custom',
        menu_row_type: row?.menu_row_type || 'custom',
        apply_to: initialApplyTo,
      }),
      display: {
        ...rowProductType.display,
        category: rowProductType.display?.category || 'flower',
        subcategory: rowProductType.display?.subcategory || 'None',
      },
      enabled: row?.enabled ?? false,
      rules: row?.rules || {
        includes: [],
        excludes: [],
      },
    },
  });

  const {
    formState: { isDirty, dirtyFields },
    setValue,
    getValues,
    watch,
  } = formMethods;

  const { display } = getValues();
  const applyToWatch = watch('apply_to' as any);

  useEffect(() => {
    // won't need this anymore after this work is done
    if (useMenuRowTypeFlag) return;

    if (row) {
      if (JANE_DEFINED_ROWS.includes(row.row_type ?? '')) {
        setValue('display.type' as any, 'dynamic');
      } else {
        // In reality we set row_type to the same as custom_display_name,
        // this fake row type is simply to show/hide the category selectors
        // TODO: we're gonna have to revisit this after we start adding more template cards
        // because this is going to get untenable REAL quick
        const hasNonEmptyArray = (checkValue: any[] | null | undefined) =>
          Array.isArray(checkValue) && checkValue.length > 0;
        const conditionCheck = canUseRules
          ? hasNonEmptyArray(row?.rules?.includes) ||
            hasNonEmptyArray(row?.rules?.excludes)
          : row.applicability_rules?.length;

        const fakeRowType = conditionCheck ? 'subcategory' : 'custom';
        setValue('display.type' as any, fakeRowType);
      }
    }
  }, [row]);

  useEffect(() => {
    if (applyToWatch !== activeApplyToMode) {
      setActiveApplyToMode(applyToWatch as ApplyToMode | undefined);
    }
  }, [applyToWatch, activeApplyToMode]);

  const onSelectProducts = (products: MenuProductForProductsTable[]) => {
    track({
      event: EventNames.EditSelectedProducts,
      initial_product_count: totalNumberProductsSelected,
      final_product_count: products.length,
    });

    const productIds = products.map(({ id }) => id);
    setProductsForMode(productIds);
    setTotalNumberProductsSelected(products.length);
  };

  useEffect(() => {
    setTotalNumberProductsSelected(selectedProductIds.length);
  }, [selectedProductIds.length]);

  useEffect(() => {
    if (createSuccess || updateSuccess) {
      toast.add({
        label: `Menu row ${isCreateMode ? 'created' : 'updated'}!`,
        variant: 'success',
      });
      closeModal();
    }
  }, [createSuccess, updateSuccess, isCreateMode]);

  const onSubmit = (data: any) => {
    const {
      display,
      custom_display_name: name,
      schedule,
      start_date,
      start_time,
      end_date,
      end_time,
      use_store_close_time,
      enabled,
      ...restData
    } = data;

    const includeScheduleOverride = data['has_schedule_overrides'] === true;
    const formattedSchedule = includeScheduleOverride
      ? formatSchedule(schedule)
      : DEFAULT_ROW_SCHEDULE;

    const menuRowType = useMenuRowTypeFlag
      ? data.menu_row_type || row?.menu_row_type || 'custom'
      : undefined;

    const rowType = useMenuRowTypeFlag
      ? JANE_DEFINED_ROWS.includes(menuRowType || '')
        ? menuRowType
        : name
      : JANE_DEFINED_ROWS.includes(row?.row_type || name)
        ? row?.row_type
        : name;

    const requestData = {
      ...restData,
      custom_display_name: name,
      display,
      row_type: rowType,
      ...(useMenuRowTypeFlag && { menu_row_type: menuRowType }),
      menu_products: getProductsToSubmit(),
      schedule: {
        ...formattedSchedule,
        end_date: end_date || null,
        enabled_date_end: convert12HourTimeTo24HourTime(end_time),
        start_date: start_date || null,
        enabled_date_start: convert12HourTimeTo24HourTime(start_time),
        use_store_close_time,
        use_store_close_time_for_end: use_store_close_time,
        enabled,
      },
    };

    if (
      !useMenuRowTypeFlag &&
      display.type !== 'dynamic' &&
      display.type !== 'custom'
    ) {
      requestData['rules'] =
        display.subcategory !== 'None' &&
        display.subcategory !== '' &&
        display.subcategory !== undefined
          ? {
              includes: [
                {
                  kinds: [
                    {
                      kind: display.category,
                      root_subtype: display.subcategory,
                    },
                  ],
                },
              ],
              excludes: [],
            }
          : {
              includes: [
                {
                  kinds: [{ kind: display.category }],
                },
              ],
              excludes: [],
            };

      requestData['applicability_rules'] =
        display.type === 'subcategory' &&
        display.subcategory !== 'None' &&
        display.subcategory !== ''
          ? [
              {
                filter_type: 'root_subtype',
                filter_value: display.subcategory,
              },
            ]
          : display.type === 'subcategory' &&
              (display.subcategory === 'None' || display.subcategory === '')
            ? [
                {
                  filter_type: 'type',
                  filter_value: display.category,
                },
              ]
            : [];
    }

    if (useMenuRowTypeFlag && applyToWatch === 'products') {
      requestData['rules'] = { includes: [], excludes: [] };
      requestData['applicability_rules'] = [];
    }

    if (useMenuRowTypeFlag && applyToWatch === 'advanced') {
      if (data.rules?.includes?.[0]?.kinds?.[0]) {
        const firstKind = data.rules.includes[0].kinds[0];
        if (firstKind?.root_subtype) {
          requestData['applicability_rules'] = [
            {
              filter_type: 'root_subtype',
              filter_value: firstKind.root_subtype,
            },
          ];
        } else if (firstKind?.kind) {
          requestData['applicability_rules'] = [
            {
              filter_type: 'type',
              filter_value: firstKind.kind,
            },
          ];
        }
      } else {
        requestData['applicability_rules'] = [];
      }
    }

    const trackSubmit = (eventProps = {}) => {
      track({
        event: EventNames.EditedStoreMenuConfig,
        action: isCreateMode ? 'create' : 'update',
        modal_name: isCreateMode
          ? ModalNames.CreateMenuRow
          : ModalNames.EditMenuRow,
        type: display.type,
        changed_attributes: Object.keys(dirtyFields),
        setting_name: 'menu row',
        successful: true,
        url: normalizePath(pathname, id),
        ...eventProps,
      });
    };

    const submitMethod = async () => {
      await (isCreateMode
        ? createMenuRow(requestData)
        : updateMenuRow(requestData));
      trackSubmit();
    };

    return catchSubmitErrors({
      submitMethod,
      requestData,
      onValidationError: (validationErrors: Record<string, unknown>) => {
        trackSubmit({
          successful: false,
          error_reason: ErrorReasons.InvalidParams,
        });
        throw new FormValidationError(
          FORM_ERROR_NAME,
          parseValidationErrors(validationErrors)
        );
      },
      callback: (errorBody) => {
        if (errorBody?.errors?.error?.includes('duplicate')) {
          // Duplication is based on the name, so show an error for that field if there is a duplicate
          throw new FormValidationError(FORM_ERROR_NAME, [
            {
              name: 'custom_display_name',
              message:
                'A menu row already exists with that name. Please change it and try again.',
            },
          ]);
        }

        trackSubmit({
          successful: false,
          error_reason: ErrorReasons.ServerError,
        });

        throw new Error(
          `Error ${
            isCreateMode ? 'creating' : 'editing'
          } menu row. Please try again.`
        );
      },
    });
  };

  const shouldShowAddProducts = useMenuRowTypeFlag
    ? applyToWatch === 'products'
    : display &&
      'type' in display &&
      !['dynamic', 'subcategory'].includes(display?.type as string);

  const shouldShowProductsCard = () => {
    if (useMenuRowTypeFlag) {
      const rowTypeValue = row ? getRowType(row) : undefined;
      if (!isCreateMode && rowTypeValue === 'specials') {
        return false;
      }
      return applyToWatch === 'products' || !isCreateMode;
    } else {
      if (!isCreateMode) return row?.row_type !== 'specials';
      else {
        return display && 'type' in display && display?.type !== 'subcategory';
      }
    }
  };

  const shouldShowConditionsCardForRowType = () => {
    if (!shouldShowConditionsCard) {
      return false;
    }

    const hiddenRowTypes = ['specials', 'sale', 'best_selling'];

    let rowTypeValue: string | undefined;
    if (row) {
      rowTypeValue = getRowType(row) ?? undefined;
    } else if (useMenuRowTypeFlag) {
      rowTypeValue = watch('menu_row_type' as any) || watch('row_type' as any);
    } else {
      rowTypeValue = watch('display.type' as any);
    }

    if (isCreateMode) {
      return !rowTypeValue || !hiddenRowTypes.includes(rowTypeValue);
    }
    return rowTypeValue && !hiddenRowTypes.includes(rowTypeValue);
  };

  const shouldShowSchedulingCard =
    !row || !JANE_DEFINED_ROWS.includes(getRowType(row) || '');

  return (
    <>
      <ConfirmWrapperWithTracking
        open
        setOpen={closeModal}
        variant="full-screen"
        hasChanges={isDirty}
        modalName={
          isCreateMode ? ModalNames.CreateMenuRow : ModalNames.EditMenuRow
        }
      >
        <Form.BaseForm
          name={`${isCreateMode ? 'Create' : 'Edit'} menu row form`}
          formMethods={formMethods}
          onSubmit={onSubmit}
          formErrorName={FORM_ERROR_NAME}
        >
          <ModalHeader
            closeModal={closeModal}
            formErrorName={FORM_ERROR_NAME}
            hasEmbeddedMenuRow={hasEmbeddedMenuRow}
            isCreateMode={isCreateMode}
            row={row}
            storeId={id}
          />
          <Modal.Content>
            <Flex justifyContent="center">
              <Flex width="866px" flexDirection="column">
                <Form.ErrorBanner name={FORM_ERROR_NAME} />
                <Card width="100%" border="grays-light" mb={40}>
                  <Card.Content>
                    <Flex p={24} flexDirection="column">
                      <DetailsCard
                        isCreateMode={isCreateMode}
                        rowProductType={rowProductType}
                        defaultRowType={
                          row ? (getRowType(row) ?? undefined) : undefined
                        }
                        row={row}
                      />
                    </Flex>
                  </Card.Content>
                </Card>
                {shouldShowConditionsCardForRowType() && (
                  <MenuRowConditionsCard
                    isLoading={storeSettingsLoading || !productTypesLoaded}
                  />
                )}
                {shouldShowProductsCard() && (
                  <ProductsCard
                    shouldShowAddProducts={shouldShowAddProducts}
                    onAddProductsClick={setProductSearchModalOpen}
                    productStockCounts={{
                      inStock: totalNumberProductsInStock,
                      outOfStock: totalNumberProductsOutOfStock,
                    }}
                    productsTable={
                      <MenuRowProductTable
                        isLoading={!isCreateMode && loadingProductsForTable}
                        selectedProducts={productsForTable}
                        totalNumberProductsSelected={
                          totalNumberProductsSelected
                        }
                      />
                    }
                  />
                )}
                {shouldShowSchedulingCard && (
                  <ScheduleCard
                    isLoading={storeSettingsLoading || !productTypesLoaded}
                    storePayload={storePayload}
                    variant="menu row"
                  />
                )}
              </Flex>
            </Flex>
          </Modal.Content>
        </Form.BaseForm>
      </ConfirmWrapperWithTracking>

      {productSearchModalOpen && (
        <ProductSearchModalUpdated
          closeModal={() => setProductSearchModalClose()}
          onSubmit={onSelectProducts}
          selectedProducts={productsForModal}
          onlyShowInStockProducts={false}
        />
      )}
    </>
  );
};

const getDefaultApplyTo = ({
  row,
  isCreateMode,
  shouldShowConditionsCard,
  useMenuRowTypeFlag,
}: {
  isCreateMode: boolean;
  row: MenuRowProps['row'];
  shouldShowConditionsCard: boolean;
  useMenuRowTypeFlag: boolean;
}) => {
  if (!shouldShowConditionsCard || !useMenuRowTypeFlag) {
    return undefined;
  }

  if (isCreateMode) {
    return 'advanced';
  }

  const hasAdvancedRules =
    (row?.rules?.includes?.[0]?.kinds?.length ?? 0) > 0 ||
    (row?.rules?.includes?.[0]?.brands?.length ?? 0) > 0 ||
    row?.rules?.product_threshold;

  const hasIndividualProductsInRules =
    (row?.rules?.includes?.[0]?.product_ids?.length ?? 0) > 0;
  const hasMenuProductsWithoutRules =
    !hasAdvancedRules &&
    !hasIndividualProductsInRules &&
    (row?.menu_products_ids?.all?.length ?? 0) > 0;

  return hasIndividualProductsInRules || hasMenuProductsWithoutRules
    ? 'products'
    : 'advanced';
};

type ProductsCardProps = {
  readonly onAddProductsClick: () => void;
  readonly productsTable: ReactNode;
  readonly productStockCounts: {
    inStock: number;
    outOfStock: number;
  };
  readonly shouldShowAddProducts?: boolean;
};

const ProductsCard = ({
  productStockCounts: productCounts,
  shouldShowAddProducts = false,
  onAddProductsClick,
  productsTable,
}: ProductsCardProps) => (
  <Card width="100%" border="grays-light" data-testid="products-card" mb={40}>
    <Card.Content>
      <Flex p={24} flexDirection="column">
        <Flex mb={32} justifyContent="space-between" alignItems="center">
          <Flex flexDirection="column">
            <Typography variant="header-bold">Products</Typography>
            <Typography variant="body" mt={8}>
              {`${productCounts.inStock} in-stock, `}
              {`${productCounts.outOfStock} out-of-stock`}
            </Typography>
          </Flex>
          {shouldShowAddProducts ? (
            <Button
              variant="secondary"
              label="Add Products"
              onClick={() => onAddProductsClick()}
            />
          ) : null}
        </Flex>
        {productsTable}
      </Flex>
    </Card.Content>
  </Card>
);
